import React, { useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Users } from "lucide-react";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { Fundraiser } from "../../../models/FundraisersGridModels";
import { FundraiserService } from "../../../services";
import { firstValueFrom } from "rxjs";
import { CreateFundraiserModel } from "../../../models";

const PromotersChartContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 40px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const PromotersChartTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PromotersChartWrapper = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PromoterBarRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const PromoterBarContainer = styled.div<{ isNoPromoter?: boolean }>`
  flex: 1;
  max-width: 70%;
  position: relative;
  height: 28px;
  background: transparent;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
`;

const PromoterBarFill = styled.div<{
  percentage: number;
  isNoPromoter?: boolean;
  minWidth?: number;
}>`
  height: 100%;
  width: ${(props) => props.percentage}%;
  background: ${(props) => (props.isNoPromoter ? "#eaeaea" : "#C9ECDD")};
  border-radius: 4px;
  transition: width 1.5s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 12px;
`;

const PromoterBarAmount = styled.div<{ isNoPromoter?: boolean }>`
  font-size: 14px;
  font-weight: 700;
  color: #4b5563;
`;

const PromoterNameLeft = styled.div`
  flex: 0 0 200px;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  text-align: right;
  padding-right: 16px;
`;

interface PromotersChartProps {
  fundraiser: CreateFundraiserModel;
}

export const PromotersChart: React.FC<PromotersChartProps> = observer(
  ({ fundraiser }) => {
    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const getPromoterChartData = () => {
      // const promoters = fundraiserStore.getFundraiserPromoters(fundraiser.id);
      // const donations = fundraiserStore.getFundraiserDonations(fundraiser.id);

      // // Calculate totals for each promoter
      // const promoterTotals = new Map();
      let noPromoterTotal = 0;

      // donations.forEach((donation) => {
      //   if (donation.promoterId) {
      //     const current = promoterTotals.get(donation.promoterId) || 0;
      //     promoterTotals.set(donation.promoterId, current + donation.amount);
      //   } else {
      //     noPromoterTotal += donation.amount;
      //   }
      // });

      // Create chart data
      let chartData: { id: string; name: string; amount: number }[] = [];

      // Add promoters with donations
      fundraiser.leaderboard?.forEach((promoter) => {
        //const amount = promoterTotals.get(promoter.id) || 0;
        if (promoter.totalRaisedAmount > 0) {
          chartData.push({
            id: promoter.id,
            name: promoter.displayName,
            amount: promoter.totalRaisedAmount,
          });
        }
      });

      // Add "No Promoter" if there are donations without promoters
      if (noPromoterTotal > 0) {
        chartData.push({
          id: "no-promoter",
          name: "No Promoter",
          amount: noPromoterTotal,
        });
      }

      // Sort by amount descending
      return chartData
        .filter((item) => item.amount > 0)
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 11); // Show "No Promoter" + top 10 promoters
    };

    const getMaxPromoterAmount = () => {
      const chartData = getPromoterChartData();
      return chartData.length > 0
        ? Math.max(...chartData.map((p) => p.amount))
        : 1;
    };

    const promoterChartData = getPromoterChartData();
    const maxAmount = getMaxPromoterAmount();

    // Calculate promoter bar widths with 75px minimum
    const calculatePromoterBarWidths = () => {
      const maxAmount = getMaxPromoterAmount();
      if (maxAmount <= 0) return [];

      return promoterChartData.map((promoter, index) => {
        const ratio = promoter.amount / maxAmount;

        // Calculate proportional bar width
        // Largest gets 70%, others scale proportionally with 75px minimum
        const maxBarWidth = 70; // 70% of container
        const minBarWidthPx = 75; // Minimum 75px for text
        const containerWidth = 500; // Approximate container width
        const minBarWidthPercent = (minBarWidthPx / containerWidth) * 100;

        let barWidth = Math.max(minBarWidthPercent, ratio * maxBarWidth);

        return {
          ...promoter,
          barPercentage: barWidth,
        };
      });
    };

    const promoterBarsData = calculatePromoterBarWidths();

    // Only render if there are promoters with payments > 0
    if (promoterChartData.length === 0) {
      return null;
    }

    return (
      <PromotersChartContainer>
        <PromotersChartTitle>
          <Users size={28} color="#10b981" />
          Top Promoters
        </PromotersChartTitle>
        <PromotersChartWrapper>
          {promoterBarsData.slice(0, 10).map((promoter, index) => {
            const percentage =
              maxAmount > 0 ? (promoter.amount / maxAmount) * 100 : 0;

            return (
              <PromoterBarRow key={promoter.id || "no-promoter"}>
                <PromoterNameLeft>{promoter.name}</PromoterNameLeft>
                <PromoterBarContainer
                  isNoPromoter={promoter.id === "no-promoter"}
                >
                  <PromoterBarFill
                    percentage={
                      promoter.id === "no-promoter" ? percentage : percentage
                    }
                    isNoPromoter={promoter.id === "no-promoter"}
                  >
                    <PromoterBarAmount
                      isNoPromoter={promoter.id === "no-promoter"}
                    >
                      {formatCurrency(promoter.amount)}
                    </PromoterBarAmount>
                  </PromoterBarFill>
                </PromoterBarContainer>
              </PromoterBarRow>
            );
          })}
        </PromotersChartWrapper>
      </PromotersChartContainer>
    );
  }
);
